// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { type ReactNode, useEffect, useMemo, useState } from "react";

import { DiffclawHttpError } from "@diffclaw/client";
import {
  Brain,
  ChartLine,
  Database,
  GitBranch,
  GitCompareArrows,
  House,
  ListChecks,
  LockKeyhole,
  LogOut,
  Moon,
  Sun,
} from "lucide-react";

import {
  clearSavedIdentity,
  CockpitApi,
  expireSavedIdentity,
  loadIdentity,
  loadRecoveryScope,
  type CockpitIdentity,
  type CockpitScope,
} from "./api.js";
import { Chronicle } from "./Chronicle.js";
import { Onboarding } from "./Onboarding.js";
import { Learning } from "./Learning.js";
import { Evaluation } from "./Evaluation.js";
import { Overview } from "./Overview.js";
import { ReviewIndex } from "./ReviewIndex.js";
import { ReviewWorkspace } from "./ReviewWorkspace.js";
import { Setup } from "./Setup.js";
import { ServiceCredentials } from "./ServiceCredentials.js";

type Route =
  | { readonly page: "service-access" }
  | { readonly page: "onboarding" }
  | { readonly page: "overview" }
  | { readonly page: "chronicle" }
  | { readonly page: "learning" }
  | { readonly page: "evaluation" }
  | { readonly page: "reviews" }
  | { readonly page: "review"; readonly reviewSeriesId: string };

function routeFromHash(): Route {
  const hash = window.location.hash.replace(/^#\/?/u, "");
  if (hash.startsWith("reviews/")) {
    return {
      page: "review",
      reviewSeriesId: decodeURIComponent(hash.slice("reviews/".length)),
    };
  }
  return hash === "service-access"
    ? { page: "service-access" }
    : hash === "onboarding"
      ? { page: "onboarding" }
      : hash === "learning"
        ? { page: "learning" }
        : hash === "chronicle"
          ? { page: "chronicle" }
          : hash === "evaluation"
            ? { page: "evaluation" }
            : hash === "reviews"
              ? { page: "reviews" }
              : { page: "overview" };
}

function invalidBrowserSession(error: unknown): boolean {
  return (
    error instanceof DiffclawHttpError &&
    (error.status === 400 || error.status === 401)
  );
}

export function App() {
  const [identity, setIdentity] = useState<CockpitIdentity | null>(() =>
    loadIdentity(),
  );
  const [recoveryScope, setRecoveryScope] = useState<CockpitScope | null>(() =>
    loadRecoveryScope(),
  );
  const [route, setRoute] = useState<Route>(() => routeFromHash());
  const [sessionStatus, setSessionStatus] = useState<string | null>(null);
  const [setupStatus, setSetupStatus] = useState<string | null>(null);
  // Distinguishing "still loading" from "could not be read" keeps the bar
  // from claiming to be loading a name it already failed to fetch.
  const [project, setProject] = useState<
    | { readonly state: "loading" }
    | { readonly state: "named"; readonly name: string }
    | { readonly state: "unavailable" }
  >({ state: "loading" });
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    const saved = localStorage.getItem("diffclaw.cockpit-theme");
    if (saved === "dark" || saved === "light") {
      return saved;
    }
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
  });

  useEffect(() => {
    const changed = () => {
      setRoute(routeFromHash());
    };
    window.addEventListener("hashchange", changed);
    return () => {
      window.removeEventListener("hashchange", changed);
    };
  }, []);

  const api = useMemo(
    () => (identity === null ? null : new CockpitApi(identity)),
    [identity],
  );

  useEffect(() => {
    document.documentElement.dataset["theme"] = theme;
    localStorage.setItem("diffclaw.cockpit-theme", theme);
  }, [theme]);

  useEffect(() => {
    if (api === null) {
      setProject({ state: "loading" });
      return;
    }
    let current = true;
    void api
      .project()
      .then(({ project: summary }) => {
        if (current) {
          setProject({ name: summary.name, state: "named" });
        }
      })
      .catch(() => {
        if (current) {
          setProject({ state: "unavailable" });
        }
      });
    return () => {
      current = false;
    };
  }, [api, route.page]);

  useEffect(() => {
    if (identity?.authentication !== "session" || api === null) {
      return;
    }
    let current = true;
    void api
      .session()
      .then((session) => {
        if (
          current &&
          (session.actor.actorId !== identity.actorId ||
            session.actor.organizationId !== identity.organizationId)
        ) {
          clearSavedIdentity();
          setRecoveryScope(null);
          setIdentity(null);
        }
      })
      .catch((error: unknown) => {
        if (current) {
          if (invalidBrowserSession(error)) {
            setRecoveryScope(expireSavedIdentity(identity));
            setIdentity(null);
            setSessionStatus(null);
            setSetupStatus("Your GitHub session expired. Sign in again.");
            return;
          }
          setSessionStatus(
            error instanceof Error
              ? `Session verification failed: ${error.message}`
              : "Session verification failed.",
          );
        }
      });
    return () => {
      current = false;
    };
  }, [api, identity]);

  function openWorkspace(nextIdentity: CockpitIdentity) {
    setSetupStatus(null);
    setRecoveryScope(null);
    setIdentity(nextIdentity);
  }

  if (identity === null || api === null) {
    return (
      <Setup
        initialAuthentication={
          setupStatus === null && recoveryScope === null
            ? "local-bootstrap"
            : "session"
        }
        initialScope={recoveryScope}
        initialStatus={setupStatus}
        onReady={openWorkspace}
      />
    );
  }
  const activeApi = api;
  const activeIdentity = identity;

  function resetIdentity() {
    clearSavedIdentity();
    setRecoveryScope(null);
    setIdentity(null);
  }

  const navigation: readonly {
    readonly href: string;
    readonly label: string;
    readonly page: Exclude<Route["page"], "review">;
    readonly renderIcon: (active: boolean) => ReactNode;
  }[] = [
    {
      renderIcon: (active) => (
        <House size={19} strokeWidth={active ? 2.2 : 1.7} />
      ),
      label: "Overview",
      page: "overview",
      href: "#/overview",
    },
    {
      renderIcon: (active) => (
        <ListChecks size={19} strokeWidth={active ? 2.2 : 1.7} />
      ),
      label: "Reviews",
      page: "reviews",
      href: "#/reviews",
    },
    {
      renderIcon: (active) => (
        <Database size={19} strokeWidth={active ? 2.2 : 1.7} />
      ),
      label: "Chronicle",
      page: "chronicle",
      href: "#/chronicle",
    },
    {
      renderIcon: (active) => (
        <Brain size={19} strokeWidth={active ? 2.2 : 1.7} />
      ),
      label: "Learning",
      page: "learning",
      href: "#/learning",
    },
    {
      renderIcon: (active) => (
        <ChartLine size={19} strokeWidth={active ? 2.2 : 1.7} />
      ),
      label: "Evaluation",
      page: "evaluation",
      href: "#/evaluation",
    },
    {
      renderIcon: (active) => (
        <GitBranch size={19} strokeWidth={active ? 2.2 : 1.7} />
      ),
      label: "Repositories",
      page: "onboarding",
      href: "#/onboarding",
    },
    {
      renderIcon: (active) => (
        <LockKeyhole size={19} strokeWidth={active ? 2.2 : 1.7} />
      ),
      label: "Service access",
      page: "service-access",
      href: "#/service-access",
    },
  ];

  async function leaveWorkspace() {
    if (activeIdentity.authentication === "local-bootstrap") {
      resetIdentity();
      return;
    }
    setSessionStatus("Signing out…");
    try {
      await activeApi.logout();
      resetIdentity();
      setSessionStatus(null);
    } catch (error) {
      if (invalidBrowserSession(error)) {
        setRecoveryScope(expireSavedIdentity(activeIdentity));
        setIdentity(null);
        setSessionStatus(null);
        setSetupStatus("Your GitHub session expired. Sign in again.");
        return;
      }
      setSessionStatus(
        error instanceof Error ? error.message : "Sign-out failed.",
      );
    }
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="#/overview" aria-label="Diffclaw overview">
          <span className="brand-mark" aria-hidden="true">
            <GitCompareArrows strokeWidth={2.2} />
          </span>
          Diffclaw
        </a>
        <span className="topbar-divider" aria-hidden="true" />
        <div className="scope" title={`Project ${identity.projectId}`}>
          <span className="scope-label">Project</span>
          <strong>
            {project.state === "named"
              ? project.name
              : project.state === "loading"
                ? "Loading…"
                : "Name unavailable"}
          </strong>
        </div>
        <span className="topbar-spacer" />
        <p className="session-status" aria-live="polite">
          {sessionStatus}
        </p>
        <button
          className="icon-button"
          onClick={() => {
            setTheme(theme === "dark" ? "light" : "dark");
          }}
          type="button"
          aria-label={`Use ${theme === "dark" ? "light" : "dark"} theme`}
        >
          {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
        </button>
        <button
          className="text-button"
          onClick={() => void leaveWorkspace()}
          type="button"
        >
          <LogOut size={16} aria-hidden="true" />
          <span>
            {identity.authentication === "session" ? "Sign out" : "Switch"}
          </span>
        </button>
      </header>
      <div className="app-body">
        <nav className="sidebar" aria-label="Cockpit">
          <p className="nav-label">Workspace</p>
          {navigation.map((item) => {
            const active =
              route.page === item.page ||
              (item.page === "reviews" && route.page === "review");
            return (
              <a
                aria-current={active ? "page" : undefined}
                href={item.href}
                key={item.page}
              >
                {item.renderIcon(active)}
                <span>{item.label}</span>
              </a>
            );
          })}
        </nav>
        <main id="main-content" className="main-content" tabIndex={-1}>
          {route.page === "service-access" ? (
            <ServiceCredentials
              api={api}
              identity={identity}
              key={`${identity.organizationId}:${identity.actorId}`}
            />
          ) : route.page === "overview" ? (
            <Overview api={api} />
          ) : route.page === "onboarding" ? (
            <Onboarding api={api} identity={identity} />
          ) : route.page === "chronicle" ? (
            <Chronicle api={api} />
          ) : route.page === "learning" ? (
            <Learning api={api} />
          ) : route.page === "evaluation" ? (
            <Evaluation api={api} />
          ) : route.page === "reviews" ? (
            <ReviewIndex api={api} />
          ) : (
            <ReviewWorkspace api={api} reviewSeriesId={route.reviewSeriesId} />
          )}
        </main>
      </div>
    </div>
  );
}
